import { useState } from "react";
import {
    AlertTriangle,
    ArrowRight,
    CalendarClock,
    MoreVertical,
    Wand2,
    XCircle,
    Send,
    User as UserIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { classifyDueState, formatDuePillLabel, scheduledPastDueTitle } from "@/lib/dueUrgency";
import type { DispatchTech } from "./dispatch-types";

// ─── MissedJobsSection ─────────────────────────────────────────────────
//
// Top-of-dispatch triage panel for jobs that were scheduled before today
// and never got marked complete. Sourced from GET /api/dispatch/missed-jobs.
// Renders nothing when count === 0.
//
// Each card is:
//   1. Drag-source — drops onto the day's TechRow swimlane the same way
//      backlog cards do (writes the job id into `dataTransfer/text/plain`,
//      mirroring `day-swimlane.tsx` TechRow.onDrop).
//   2. Quick-action surface — Auto-fill (clear schedule + auto-schedule),
//      Move to today, Move to tomorrow, plus an overflow menu for
//      Pick date/time, Send to office review, Cancel.
//
// All actions are passed in as callbacks so the shell owns the actual
// PATCH / POST flow (matching the existing handleDrop / handleUnschedule
// pattern).

export interface MissedJob {
    id: string;
    title: string;
    status: string;
    customerName: string | null;
    siteName: string | null;
    siteAddress: string | null;
    siteCity: string | null;
    siteRegion: string | null;
    siteLatitude: number | null;
    siteLongitude: number | null;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    assignedTo: string | null;
    techName: string | null;
    daysMissed: number;
    priority: string | null;
    jobTypeName: string | null;
    dueByDate: string | null;
    /** When set, office has already pinged the assigned tech to close
     *  this out — surfaces as a "Pinged tech · awaiting close-out" pill
     *  in the triage modal so the office doesn't keep nagging itself. */
    pendingCloseoutAt: string | null;
    pendingCloseoutNote: string | null;
    pendingCloseoutByName: string | null;
    /** Derived server-side from pending_closeout_at. */
    decisionStatus: "awaiting_office" | "awaiting_tech_closeout";
    /** A tech started it (arrived, started work, or saved a result). The
     *  overdue sweep never moves these to office review (11/4); they wait
     *  here, tagged "Still in progress", for the office to chase. */
    stillInProgress?: boolean;
}

export interface MissedJobsResponse {
    jobs: MissedJob[];
    counts: {
        total: number;
        oldestDays: number;
        /** Rows the office still needs to decide on (no
         *  pending_closeout_at). The triage modal nags only on these. */
        awaitingOffice: number;
        /** Rows the office already pinged the tech about. The top bar
         *  surfaces both buckets so the office can chase a slow tech. */
        awaitingTechCloseout: number;
        /** How many of these a tech started (see MissedJob.stillInProgress). */
        stillInProgress?: number;
    };
}

export interface MissedJobActionHandlers {
    onAutoFill: (job: MissedJob) => void;
    onMoveToToday: (job: MissedJob, techId: string) => void;
    onMoveToTomorrow: (job: MissedJob, techId: string) => void;
    onPickDateTime: (job: MissedJob, isoStart: string, techId: string) => void;
    onSendToOfficeReview: (job: MissedJob, reason: string) => void;
    onCancel: (job: MissedJob, reason: string) => void;
    onOpenJob: (job: MissedJob) => void;
}

export function MissedJobsSection({
    response,
    techs,
    onAction,
    busy,
    sectionId,
    showAutoFill = true,
}: {
    response: MissedJobsResponse | undefined;
    techs: DispatchTech[];
    onAction: MissedJobActionHandlers;
    /** When set, action buttons render a spinner instead of the icon. */
    busy?: { jobId: string | null };
    /** id attribute on the wrapper so the dashboard banner can hash-link
     *  in via /dispatch?section=missed and we can scroll into view. */
    sectionId?: string;
    /** AUTOMATION PRIVACY (G6.1 §8): the Auto-fill button is an automation
     *  affordance — pass false to hide it for users without dispatch.manage. */
    showAutoFill?: boolean;
}) {
    const allJobs = response?.jobs ?? [];
    // "Still in progress" filter (11/4): the started jobs the overdue sweep
    // now leaves here instead of parking them in office review.
    const [onlyInProgress, setOnlyInProgress] = useState(false);
    const inProgressCount = allJobs.filter((j) => j.stillInProgress).length;
    const showOnlyInProgress = onlyInProgress && inProgressCount > 0;
    const jobs = showOnlyInProgress ? allJobs.filter((j) => j.stillInProgress) : allJobs;
    if (allJobs.length === 0) return null;

    const oldest = response?.counts.oldestDays ?? 0;
    const tone =
        oldest >= 2
            ? "border-l-red-500 bg-red-50/60 dark:bg-red-950/20"
            : "border-l-amber-500 bg-amber-50/60 dark:bg-amber-950/20";

    return (
        <section id={sectionId} className={`rounded-xl border border-border/70 border-l-4 ${tone} px-4 py-3 shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <h2 className="text-sm font-bold text-foreground">Missed Jobs ({allJobs.length})</h2>
                    {oldest > 0 && (
                        <Badge variant="outline" className="text-[11px]">
                            Oldest: {oldest} day{oldest !== 1 ? "s" : ""}
                        </Badge>
                    )}
                    {inProgressCount > 0 && (
                        <button
                            type="button"
                            data-testid="missed-still-in-progress-filter"
                            aria-pressed={showOnlyInProgress}
                            title="Jobs a tech started but hasn't finished. Call the tech; these are never moved to Office Review on their own."
                            onClick={() => setOnlyInProgress((v) => !v)}
                            className={cn(
                                "rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors",
                                showOnlyInProgress
                                    ? "border-sky-600 bg-sky-600 text-white"
                                    : "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200",
                            )}
                        >
                            Still in progress ({inProgressCount})
                        </button>
                    )}
                </div>
                <span className="text-[11px] text-foreground/70">
                    Drag a card onto a tech, or use the actions below to triage.
                </span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
                {jobs.map((j) => (
                    <MissedJobCard
                        key={j.id}
                        job={j}
                        techs={techs}
                        onAction={onAction}
                        busy={busy?.jobId === j.id}
                        showAutoFill={showAutoFill}
                    />
                ))}
            </div>
        </section>
    );
}

function MissedJobCard({
    job,
    techs,
    onAction,
    busy,
    showAutoFill,
}: {
    job: MissedJob;
    techs: DispatchTech[];
    onAction: MissedJobActionHandlers;
    busy: boolean;
    showAutoFill: boolean;
}) {
    const [techMenuFor, setTechMenuFor] = useState<"today" | "tomorrow" | null>(null);
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [escalateOpen, setEscalateOpen] = useState<"review" | "cancel" | null>(null);

    const dragHandle = (e: React.DragEvent<HTMLDivElement>) => {
        // Match the protocol used by `BacklogPanel` and the swimlane —
        // payload is the bare job id as text/plain.
        e.dataTransfer.setData("text/plain", job.id);
        e.dataTransfer.effectAllowed = "move";
    };

    const summary = [job.customerName, job.siteName].filter(Boolean).join(" — ");
    const location = [job.siteCity, job.siteRegion].filter(Boolean).join(", ");
    const daysLabel = `${job.daysMissed} day${job.daysMissed !== 1 ? "s" : ""} late`;
    const dueState = classifyDueState(job.dueByDate, job.scheduledStart);

    return (
        <div
            draggable
            onDragStart={dragHandle}
            className="relative shrink-0 w-72 rounded-lg border border-border/70 bg-card shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
        >
            <div className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <button type="button" onClick={() => onAction.onOpenJob(job)} className="text-left w-full">
                            <p className="text-sm font-semibold truncate text-foreground hover:text-primary transition-colors">
                                {job.title || "Untitled job"}
                            </p>
                        </button>
                        {summary && <p className="text-[11px] text-foreground/80 truncate">{summary}</p>}
                        {(location || job.jobTypeName) && (
                            <p className="text-[11px] text-foreground/65 truncate">
                                {[job.jobTypeName, location].filter(Boolean).join(" · ")}
                            </p>
                        )}
                        {dueState && job.dueByDate && (
                            <span
                                className={cn(
                                    "mt-1 inline-flex shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                                    dueState === "overdue"
                                        ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
                                        : dueState === "scheduled_past_due"
                                          ? "bg-slate-200 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300"
                                          : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
                                )}
                                title={
                                    dueState === "overdue"
                                        ? "Past due"
                                        : dueState === "scheduled_past_due"
                                          ? scheduledPastDueTitle(job.dueByDate)
                                          : `Due ${job.dueByDate}`
                                }
                            >
                                {formatDuePillLabel(job.dueByDate)}
                            </span>
                        )}
                    </div>
                    <Badge
                        variant="outline"
                        className="text-[10px] shrink-0 border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300"
                    >
                        {daysLabel}
                    </Badge>
                </div>

                {job.stillInProgress && (
                    <span className="inline-flex rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800 dark:bg-sky-500/15 dark:text-sky-200">
                        Still in progress
                    </span>
                )}
                {job.techName && (
                    <p className="flex items-center gap-1 text-[11px] text-foreground/70 truncate">
                        <UserIcon className="h-3 w-3 shrink-0" />
                        Was on {job.techName}
                    </p>
                )}

                <div className="flex items-center gap-1.5 pt-1">
                    {showAutoFill && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[11px] gap-1"
                            disabled={busy}
                            onClick={() => onAction.onAutoFill(job)}
                            title="Re-queue for the auto-scheduler"
                        >
                            <Wand2 className="h-3 w-3" /> Auto-fill
                        </Button>
                    )}
                    <div className="relative">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[11px] gap-1"
                            disabled={busy}
                            onClick={() => setTechMenuFor((prev) => (prev === "today" ? null : "today"))}
                        >
                            <ArrowRight className="h-3 w-3" /> Today
                        </Button>
                        {techMenuFor === "today" && (
                            <TechPicker
                                techs={techs}
                                onPick={(techId) => {
                                    setTechMenuFor(null);
                                    onAction.onMoveToToday(job, techId);
                                }}
                                onClose={() => setTechMenuFor(null)}
                            />
                        )}
                    </div>
                    <div className="relative">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[11px] gap-1"
                            disabled={busy}
                            onClick={() => setTechMenuFor((prev) => (prev === "tomorrow" ? null : "tomorrow"))}
                        >
                            <CalendarClock className="h-3 w-3" /> Tomorrow
                        </Button>
                        {techMenuFor === "tomorrow" && (
                            <TechPicker
                                techs={techs}
                                onPick={(techId) => {
                                    setTechMenuFor(null);
                                    onAction.onMoveToTomorrow(job, techId);
                                }}
                                onClose={() => setTechMenuFor(null)}
                            />
                        )}
                    </div>
                    <div className="relative ml-auto">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => setOverflowOpen((v) => !v)}
                            title="More actions"
                        >
                            <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                        {overflowOpen && (
                            <>
                                <div className="fixed inset-0 z-30" onClick={() => setOverflowOpen(false)} />
                                <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-border bg-popover shadow-lg z-40 overflow-hidden">
                                    <DateTimePickerMenuItem
                                        techs={techs}
                                        onPick={(isoStart, techId) => {
                                            setOverflowOpen(false);
                                            onAction.onPickDateTime(job, isoStart, techId);
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-secondary/70"
                                        onClick={() => {
                                            setOverflowOpen(false);
                                            setEscalateOpen("review");
                                        }}
                                    >
                                        <Send className="h-3.5 w-3.5" />
                                        Send to office review
                                    </button>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-secondary/70"
                                        onClick={() => {
                                            setOverflowOpen(false);
                                            setEscalateOpen("cancel");
                                        }}
                                    >
                                        <XCircle className="h-3.5 w-3.5" />
                                        Cancel job
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {escalateOpen && (
                <ReasonPrompt
                    title={escalateOpen === "review" ? "Send to office review" : "Cancel job"}
                    placeholder={
                        escalateOpen === "review"
                            ? "Why is this going to office review?"
                            : "Why is this job being cancelled?"
                    }
                    onCancel={() => setEscalateOpen(null)}
                    onSubmit={(reason) => {
                        if (escalateOpen === "review") onAction.onSendToOfficeReview(job, reason);
                        else onAction.onCancel(job, reason);
                        setEscalateOpen(null);
                    }}
                />
            )}
        </div>
    );
}

export function TechPicker({
    techs,
    onPick,
    onClose,
}: {
    techs: DispatchTech[];
    onPick: (techId: string) => void;
    onClose: () => void;
}) {
    return (
        <>
            <div className="fixed inset-0 z-30" onClick={onClose} />
            <div className="absolute left-0 top-full mt-1 w-48 rounded-lg border border-border bg-popover shadow-lg z-40 overflow-hidden max-h-64 overflow-y-auto">
                {techs.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-foreground/70">No technicians on duty.</div>
                ) : (
                    techs.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-secondary/70"
                            onClick={() => onPick(t.id)}
                        >
                            <UserIcon className="h-3 w-3" />
                            {`${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() || t.email || "Tech"}
                        </button>
                    ))
                )}
            </div>
        </>
    );
}

export function DateTimePickerMenuItem({
    techs,
    onPick,
}: {
    techs: DispatchTech[];
    onPick: (isoStart: string, techId: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("09:00");
    const [techId, setTechId] = useState<string>(techs[0]?.id ?? "");
    if (!open) {
        return (
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-secondary/70"
                onClick={() => setOpen(true)}
            >
                <CalendarClock className="h-3.5 w-3.5" />
                Pick date / time…
            </button>
        );
    }
    return (
        <div className="px-3 py-2 space-y-1.5 border-b border-border/60">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70">Pick date / time</div>
            <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
            />
            <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
            />
            <select
                value={techId}
                onChange={(e) => setTechId(e.target.value)}
                className="w-full rounded border border-input bg-background px-2 py-1 text-xs"
            >
                <option value="">— Pick tech —</option>
                {techs.map((t) => (
                    <option key={t.id} value={t.id}>
                        {`${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() || t.email || "Tech"}
                    </option>
                ))}
            </select>
            <Button
                size="sm"
                className="w-full h-7 text-[11px]"
                disabled={!date || !techId}
                onClick={() => {
                    if (!date || !techId) return;
                    // Local-time interpretation: matches how the existing
                    // dispatch grid drops a job — server stores the
                    // resulting ISO timestamp as-is.
                    const iso = new Date(`${date}T${time}:00`).toISOString();
                    onPick(iso, techId);
                    setOpen(false);
                }}
            >
                Schedule
            </Button>
        </div>
    );
}

export function ReasonPrompt({
    title,
    placeholder,
    onCancel,
    onSubmit,
}: {
    title: string;
    placeholder: string;
    onCancel: () => void;
    onSubmit: (reason: string) => void;
}) {
    const [reason, setReason] = useState("");
    return (
        <>
            <div className="fixed inset-0 z-40 bg-black/30" onClick={onCancel} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <div className="pointer-events-auto w-full max-w-sm rounded-xl border border-border bg-card shadow-xl p-4 space-y-3">
                    <h3 className="text-sm font-semibold">{title}</h3>
                    <textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={placeholder}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button size="sm" disabled={reason.trim().length === 0} onClick={() => onSubmit(reason.trim())}>
                            Confirm
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
